import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useMemo, ReactNode, createContext } from 'react';

// 전역 스낵바 제어 함수
let showGlobalSnackbar: (options: any) => void;
let enqueueGlobalSnackbar: ((message: string, options?: any) => void) | undefined;

/**
 * 이미지/첨부 업로드 실패 안내 스낵바 공통 옵션.
 * - autoHideDuration: 전역 notistack 기본값(1초)보다 길게 — 모바일에서 짧게 지나가 “안 뜬 것”처럼 보이는 것을 방지.
 * - anchorOrigin bottom/right: 댓글 입력바(하단 fixed)와 겹치지 않도록 우하단 고정.
 * (컨테이너 z-index는 snackbar-provider.tsx GlobalStyles에서 Dialog보다 위로 올린다.)
 */
export const UPLOAD_FAIL_SNACKBAR_OPTIONS = {
  variant: 'default' as const,
  autoHideDuration: 4000,
  anchorOrigin: { horizontal: 'right' as const, vertical: 'bottom' as const },
  style: {
    backgroundColor: 'var(--grey-900)',
    color: 'var(--white-main)',
  },
};

/** 학습창 질문 시트 업로드 실패만 흰 배경·검은 글씨. 공용 옵션 색은 바꾸지 않는다. */
export const LEARN_WINDOW_UPLOAD_SNACKBAR_OPTIONS = {
  variant: 'default' as const,
  autoHideDuration: 4000,
  anchorOrigin: { horizontal: 'right' as const, vertical: 'bottom' as const },
  style: {
    backgroundColor: 'var(--white-main)',
    color: 'var(--black-main)',
  },
};

interface SnackbarProviderProps {
  children: ReactNode;
}

const SnackbarContext = createContext({});

export const SnackbarProvider = ({ children }: SnackbarProviderProps) => {
  const { t } = useTranslation();

  const { enqueueSnackbar } = useSnackbar();

  enqueueGlobalSnackbar = (message: string, options?: any) => {
    enqueueSnackbar(message, options);
  };

  showGlobalSnackbar = (options: any) => {
    enqueueSnackbar(t('learner.3988'), options);
  };

  // Context 값 메모이제이션
  const contextValue = useMemo(() => ({}), []);

  return <SnackbarContext.Provider value={contextValue}>{children}</SnackbarContext.Provider>;
};

// 스낵바를 전역적으로 띄우는 유틸리티 함수
export const showErrorSnackbar = (options: any = { variant: 'default' }) => {
  if (showGlobalSnackbar) {
    showGlobalSnackbar(options);
  }
};

/** React 트리 밖(common.ts 등)에서 사용자 메시지 스낵바를 띄울 때 사용한다. */
export const showAppSnackbar = (message: string, options?: any) => {
  enqueueGlobalSnackbar?.(message, options);
};
